import { motion } from "framer-motion";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import React, { useRef, useState, useEffect } from "react";
import Images from "../../Images";
import { useSelector, useDispatch } from "react-redux";
import { setUser, clearUser } from "../../redux/UserSlice";
import { useMutation } from "react-query";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { removeToken, setToken } from "../../utils/token";
import Config from "../../Config";
import { resendOtp } from "../../requests/auth/Authentication";
import "react-toastify/dist/ReactToastify.css";

const validationSchema = Yup.object({
  otp_one: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_two: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_three: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
  otp_four: Yup.string()
    .required("Required")
    .matches(/^[0-9]$/, "Must be a single digit"),
});

const initialValues = {
  otp_one: "",
  otp_two: "",
  otp_three: "",
  otp_four: "",
};

function VerifyOtp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user) || null;
  
  const [vendorData, setVendorData] = useState(() => {
    // Only load from localStorage if user is authenticated
    if (user && user.isAuthenticated !== false) {
      const storedData = localStorage.getItem('vendorData');
      if (storedData) {
        return JSON.parse(storedData);
      }
    }
    return {
      vendor_slug: user?.vendor_slug || user?.data?.data?.vendor_slug || null,
      email: user?.email || user?.data?.data?.email || null
    };
  });
  
  const [isVerifying, setIsVerifying] = useState(false);

  const verifyOtpMutation = useMutation({
    mutationFn: async (otp) => {
      setIsVerifying(true);
      try {
        const response = await axios.post(`${Config.backendDomain}vendor/verify/account`, {
          vendor_slug: vendorData.vendor_slug, 
          otp: parseInt(otp)
        });
        console.log("Verification API Response:", response.data);
        return response;
      } catch (error) {
        console.error("Verification API Error:", error.response?.data || error);
        throw error;
      } finally {
        setIsVerifying(false);
      }
    },
    onSuccess: (response) => {
      const responseData = response?.data?.data;

      if (responseData?.in_error) {
        const errorMessage = responseData?.reason || "Sorry, OTP could not be verified.";
        toast.error(errorMessage);
        setIsVerifying(false);
        return;
      }

      if (responseData?.data) {
        const userData = responseData.data;
        console.log("User Data after verification:", userData);
        
        const vendorDataToStore = {
          vendor_slug: userData.vendor_slug || userData?.data?.vendor_slug,
          email: userData.email || userData?.data?.email
        };
        localStorage.setItem('vendorData', JSON.stringify(vendorDataToStore));
        
        dispatch(setUser(userData));
        if (userData.token) {
          setToken(userData.token);
        }

        if (!userData.email_verified_at && !userData.phone_number_verified_at) {
          navigate('/verify-otp');
          return;
        } else {
          toast.success("Account verified successfully!");
          navigate("/dashboard");
        }
      } else {
        console.log("No user data in verification response");
        toast.error("Verification failed: Invalid response from server");
      }
    },
    onError: (error) => {
      console.error("Verification Error:", error);
      const errorData = error.response?.data;
      let errorMessage;

      if (errorData?.data?.reason) {
        errorMessage = errorData.data.reason;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = "An error occurred during verification. Please try again.";
      }

      console.log("Displaying error message:", errorMessage);
      toast.error(errorMessage);
      setIsVerifying(false);
    }
  });

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleKeyUp = (e, fieldIndex, setFieldValue) => {
    const value = e.target.value;

    if (value.match(/^[0-9]$/)) {
      setFieldValue(`otp_${fieldIndex + 1}`, value);
      if (fieldIndex < 3) inputRefs[fieldIndex + 1].current.focus();
    } else if (e.key === "Backspace") {
      setFieldValue(`otp_${fieldIndex + 1}`, "");
      if (fieldIndex > 0) inputRefs[fieldIndex - 1].current.focus();
    }
  };

  const handleLogout = () => {
    dispatch(clearUser());
    if(localStorage.getItem("dealboxx_token")){
      removeToken();
    }
    // Clear vendor data from localStorage on logout
    localStorage.removeItem('vendorData');
    navigate("/signin");
  };

  const resendOtpMutation = useMutation(resendOtp, 
    {
      onSuccess: (response) => {
        const result = response?.data?.data;
        console.log("result", result)

        if (result?.in_error === true) {
          let errorMessage = result?.reason || "Failed to send OTP";
          
          switch (result?.status_code) {
            case "004":
              if (result?.reason === "User cannot be found") {
                errorMessage = "No account found with these credentials. Please check and try again.";
              }
              break;
            default:
              errorMessage = result?.reason || "Something went wrong. Please try again.";
          }
          
          toast.error(errorMessage);
          return;
        }
        toast.success(result?.reason || "OTP sent successfully");
      },
      onError: (error) => {
        const errorResponse = error?.response?.data;
        const errorMessage = errorResponse?.reason 
          || errorResponse?.message 
          || "Unable to send OTP. Please try again later.";
        
        toast.error(errorMessage);
      },

  })

  const handleResendOtpSubmit = ()=>{
    resendOtpMutation.mutate({
      email: vendorData.email, // Use from state instead of directly from user
    });
  }

  // Update vendorData if user changes and store in localStorage
  useEffect(() => {
    if (user && user.isAuthenticated !== false) {
      const newVendorData = {
        vendor_slug: user?.vendor_slug || user?.data?.data?.vendor_slug || vendorData.vendor_slug,
        email: user?.email || user?.data?.data?.email || vendorData.email
      };
      setVendorData(newVendorData);
      localStorage.setItem('vendorData', JSON.stringify(newVendorData));
    }
  }, [user]);

  // Add loading state for resend button
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResendClick = async () => {
    if (cooldown > 0) return;
    
    setIsResending(true);
    await handleResendOtpSubmit();
    setIsResending(false);
    setCooldown(60); // Set 60 seconds cooldown
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
        theme="colored"
      />
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Vendor Account Security</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Business Verification</span>
          </div>
        </motion.div>

        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_four}
              alt="otp-verification"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
            />
            
            <motion.div 
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="px-8 text-center">
                <motion.h2 
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Verify Your Business
                </motion.h2>
                <motion.p 
                  className="mx-auto mb-8 max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Complete your vendor registration with secure verification
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <button
        onClick={handleLogout}
        className="absolute top-4 right-4 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      >
        Logout
      </button>

      <motion.div 
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

  

        <motion.div
          className="mb-8 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Vendor Verification
          </h1>
          <p className="text-[#808080] text-base">
            We sent a verification code to your email. Enter the code to activate your vendor account.
          </p>
        </motion.div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const otp = `${values.otp_one}${values.otp_two}${values.otp_three}${values.otp_four}`;
            verifyOtpMutation.mutate(otp);
          }}
        >
          {({ values, setFieldValue, isValid }) => (
            <Form className="w-full">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="w-full"
              >
                <div className="flex gap-4 justify-center items-center mb-8">
                  {["otp_one", "otp_two", "otp_three", "otp_four"].map(
                    (field, index) => (
                      <motion.div
                        key={field}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                        className="w-16 h-16 sm:w-20 sm:h-20"
                      >
                        <Field name={field}>
                          {({ field }) => (
                            <input
                              {...field}
                              ref={inputRefs[index]}
                              type="text"
                              inputMode="numeric"
                              maxLength="1"
                              autoComplete="off"
                              value={values[field.name] || ""}
                              onKeyUp={(e) => handleKeyUp(e, index, setFieldValue)}
                              className="w-full h-full text-3xl font-bold text-center rounded-2xl border-2 
                                focus:outline-none focus:ring-4 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] 
                                transition-all duration-300 hover:border-[#FF6C6C]/50"
                              style={{
                                borderColor: values[field.name] !== "" 
                                  ? "#FF6C6C" 
                                  : field.name === document.activeElement?.name 
                                    ? "#FF6C6C" 
                                    : "#E5E7EB",
                                backgroundColor: values[field.name] !== "" 
                                  ? "#FFF5F5" 
                                  : "white",
                                color: values[field.name] !== "" 
                                  ? "#FF6C6C" 
                                  : "#374151",
                                boxShadow: values[field.name] !== "" 
                                  ? "0 4px 12px rgba(255,108,108,0.1)" 
                                  : field.name === document.activeElement?.name 
                                    ? "0 0 0 4px rgba(255,108,108,0.1)" 
                                    : "none",
                                transform: field.name === document.activeElement?.name 
                                  ? "translateY(-2px)" 
                                  : "translateY(0)",
                              }}
                            />
                          )}
                        </Field>
                      </motion.div>
                    )
                  )}
                </div>

                <motion.div 
                  className="flex justify-center mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <p className="text-[#808080]">
                    Didn't receive code?{" "}
                    <motion.button
                      type="button"
                      onClick={handleResendClick}
                      disabled={isResending || cooldown > 0}
                      className={`text-[#FF6C6C] font-semibold hover:text-[#ff8b8b] disabled:text-[#ff6c6c80] disabled:cursor-not-allowed`}
                      whileHover={{ scale: isResending || cooldown > 0 ? 1 : 1.05 }}
                      whileTap={{ scale: isResending || cooldown > 0 ? 1 : 0.95 }}
                    >
                      {isResending ? (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="inline-flex items-center"
                        >
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#FF6C6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Resending...
                        </motion.span>
                      ) : cooldown > 0 ? (
                        `Resend (${cooldown}s)`
                      ) : (
                        "Resend"
                      )}
                    </motion.button>
                  </p>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={Object.values(values).some((val) => !val) || isVerifying}
                  className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                    Object.values(values).some((val) => !val) || isVerifying
                      ? "bg-[#ff6c6c9a] text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white shadow-lg hover:shadow-xl"
                  }`}
                  whileHover={!Object.values(values).some((val) => !val) && !isVerifying ? { scale: 1.02 } : {}}
                  whileTap={!Object.values(values).some((val) => !val) && !isVerifying ? { scale: 0.98 } : {}}
                >
                  {isVerifying ? (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="inline-flex items-center"
                    >
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block mr-2"
                      >
                        ⟳
                      </motion.span>
                      Verifying...
                    </motion.span>
                  ) : (
                    "Verify OTP"
                  )}
                </motion.button>
              </motion.div>
            </Form>
          )}
        </Formik>

        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Business Verified</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📱</div>
              <div className="text-xs text-gray-600">Instant Access</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🚀</div>
              <div className="text-xs text-gray-600">Start Selling</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default VerifyOtp;
